import { useState, useRef, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Move, ZoomIn, ZoomOut, RotateCcw, Check } from "lucide-react";

export const ImagePositionEditor = ({ 
  isOpen, 
  onClose, 
  imageUrl, 
  initialPosition = { x: 0, y: 0, scale: 1 },
  onSave,
  title = "Adjust Photo Position"
}) => {
  // Position as percentages, scale starts at 1 (fit to fill)
  const [position, setPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const containerRef = useRef(null);
  const lastTouchDistance = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setPosition(initialPosition);
      setImageLoaded(false);
    }
  }, [isOpen, initialPosition]);

  // Load image to get dimensions
  useEffect(() => {
    if (imageUrl && isOpen) {
      const img = new Image();
      img.onload = () => {
        setImageDimensions({ width: img.width, height: img.height });
        setImageLoaded(true);
      };
      img.src = imageUrl;
    }
  }, [imageUrl, isOpen]);

  // Mouse/Touch drag handlers
  const handleDragStart = useCallback((clientX, clientY) => {
    setIsDragging(true);
    setDragStart({ x: clientX, y: clientY });
    setStartPos({ x: position.x, y: position.y });
  }, [position]);

  const handleDragMove = useCallback((clientX, clientY) => {
    if (!isDragging || !containerRef.current) return;
    
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    
    // Calculate delta as percentage of container size (inverted for natural drag feel)
    const deltaX = ((dragStart.x - clientX) / rect.width) * 100;
    const deltaY = ((dragStart.y - clientY) / rect.height) * 100;
    
    // Limit movement based on zoom level
    const maxMove = 50 * position.scale;
    setPosition(prev => ({
      ...prev,
      x: Math.max(-maxMove, Math.min(maxMove, startPos.x + deltaX)),
      y: Math.max(-maxMove, Math.min(maxMove, startPos.y + deltaY))
    }));
  }, [isDragging, dragStart, startPos, position.scale]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Mouse events
  const handleMouseDown = (e) => {
    e.preventDefault();
    handleDragStart(e.clientX, e.clientY);
  };

  const handleMouseMove = (e) => {
    handleDragMove(e.clientX, e.clientY);
  };

  const handleMouseUp = () => {
    handleDragEnd();
  };

  // Touch events
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      handleDragStart(e.touches[0].clientX, e.touches[0].clientY);
    } else if (e.touches.length === 2) {
      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      lastTouchDistance.current = distance;
    }
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    if (e.touches.length === 1 && isDragging) {
      handleDragMove(e.touches[0].clientX, e.touches[0].clientY);
    } else if (e.touches.length === 2) {
      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      if (lastTouchDistance.current) {
        const delta = distance - lastTouchDistance.current;
        setPosition(prev => ({
          ...prev,
          scale: Math.min(3, Math.max(1, prev.scale + delta * 0.005))
        }));
      }
      lastTouchDistance.current = distance;
    }
  };

  const handleTouchEnd = () => {
    handleDragEnd();
    lastTouchDistance.current = null;
  };

  // Scroll wheel zoom
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setPosition(prev => ({
      ...prev,
      scale: Math.min(3, Math.max(1, prev.scale + delta))
    }));
  };

  // Button controls
  const zoomIn = () => {
    setPosition(prev => ({
      ...prev,
      scale: Math.min(3, prev.scale + 0.25)
    }));
  };

  const zoomOut = () => {
    setPosition(prev => ({
      ...prev,
      scale: Math.max(1, prev.scale - 0.25)
    }));
  };

  const resetPosition = () => {
    setPosition({ x: 0, y: 0, scale: 1 });
  };

  const handleSave = () => {
    onSave(position);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-card p-0 overflow-hidden">
        <DialogHeader className="p-4 pb-2">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Move className="w-4 h-4" />
            {title}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Drag to move • Pinch or scroll to zoom
          </p>
        </DialogHeader>
        
        {/* Image editor area */}
        <div 
          ref={containerRef}
          className="relative w-full h-72 bg-black overflow-hidden cursor-grab active:cursor-grabbing select-none"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onWheel={handleWheel}
        >
          {imageUrl && (
            <img
              src={imageUrl}
              alt="Adjust position"
              className="absolute pointer-events-none w-full h-full"
              style={{
                objectFit: 'cover',
                objectPosition: `${50 + position.x}% ${50 + position.y}%`,
                transform: `scale(${position.scale})`,
                transformOrigin: 'center center',
                transition: isDragging ? 'none' : 'transform 0.1s ease-out'
              }}
              draggable={false}
            />
          )}
          
          {/* Center guide lines */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/20" />
            <div className="absolute top-1/2 left-0 right-0 h-px bg-white/20" />
          </div>
          
          {/* Crop area indicator */}
          <div className="absolute inset-4 border-2 border-white/30 rounded-lg pointer-events-none" />
        </div>
        
        {/* Controls */}
        <div className="p-4 flex items-center justify-between border-t border-charcoal/10">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={zoomOut}
              disabled={position.scale <= 1}
              className="h-9 w-9 p-0"
            >
              <ZoomOut className="w-4 h-4" />
            </Button>
            <span className="text-sm text-muted-foreground w-12 text-center">
              {Math.round(position.scale * 100)}%
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={zoomIn}
              disabled={position.scale >= 3}
              className="h-9 w-9 p-0"
            >
              <ZoomIn className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={resetPosition}
              className="h-9 px-3"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Reset
            </Button>
          </div>
          
          <Button onClick={handleSave} className="gap-2">
            <Check className="w-4 h-4" />
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Helper function to get CSS for displaying the cropped image
export const getImageTransform = (position) => {
  if (!position) return { objectPosition: 'center center' };
  return {
    objectFit: 'cover',
    objectPosition: `${50 + (position.x || 0)}% ${50 + (position.y || 0)}%`,
    transform: `scale(${position.scale || 1})`,
    transformOrigin: 'center center'
  };
};

export default ImagePositionEditor;
